import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell, Loader2, MessageCircle, Sparkles, Users,
  Share2, Heart, HandHeart, ShieldAlert, HandHelping, X, RefreshCw,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getNotifications, markNotificationsRead, sendThanks, deleteNotification } from '../lib/api';

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const Avatar = ({ name, picture, size }) => {
  // Size defaults to 36px (`w-9 h-9`) for the main row. Consolidated
  // "Show who" list rows pass `size={20}` for a compact roster look.
  const dim = typeof size === 'number' && size > 0 ? size : 36;
  const style = { width: dim, height: dim };
  const cls = 'rounded-full flex-shrink-0 object-cover';
  if (picture) {
    return <img src={picture} alt={name} className={cls} style={style} />;
  }
  const initial = (name || 'U').trim()[0]?.toUpperCase();
  const fontSize = dim <= 20 ? 10 : 14;
  return (
    <div
      className="rounded-full flex-shrink-0 bg-gradient-to-br from-amber-400 to-purple-500 text-white flex items-center justify-center font-semibold"
      style={{ ...style, fontSize }}
    >
      {initial}
    </div>
  );
};

const NotificationRow = ({ item, isUnread, onThanks, onDelete, isExpanded, onToggleExpanded }) => {
  const t = item.type;
  const isComment = t === 'new_comment';
  const isNewPrayer = t === 'new_prayer';
  const isRandomShare = t === 'random_share_received';
  const isRandomOpened = t === 'random_share_opened';
  const isThanks = t === 'thanks_reply';
  const isAdminNotify = t === 'admin_notify_user';
  const isAdminDaily = t === 'admin_daily_summary';
  const isPrayerWatched = t === 'prayer_watched';
  // Running-count aware display. `_watchCount` on consolidated rows
  // is the SUM across all viewers; `meta.watch_count` on a single
  // row is that viewer's own repeat count. Both default to 1 so the
  // "1 time" phrasing is never rendered.
  const singleWatchCount = Math.max(1, Number(item?.meta?.watch_count) || 1);
  const watchCount = item?._isConsolidatedWatch
    ? (item._watchCount || 1)
    : singleWatchCount;
  const uniqueViewers = item?._isConsolidatedWatch
    ? (item._uniqueViewerCount || 1)
    : 1;
  const isConsolidatedWatch = Boolean(item?._isConsolidatedWatch) && uniqueViewers > 1;

  const thanksSent = Boolean((item.meta || {}).thanks_sent);

  // Best-effort destination: prayer page if we have one, otherwise home.
  const to = item.prayer_id ? `/prayer/${item.prayer_id}` : '/';

  const label = (() => {
    if (isComment) return <>commented on <span className="font-medium">{item.prayer_title}</span></>;
    if (isNewPrayer) return <>shared a new prayer: <span className="font-medium">{item.prayer_title}</span></>;
    if (isRandomShare) return <>shared a random prayer with you: <span className="font-medium">{item.prayer_title}</span></>;
    if (isRandomOpened) return <>{item.text}</>;
    if (isPrayerWatched) {
      // Multi-viewer consolidated: "@a, @b and 3 others prayed for
      // you 7 times · Prayer title" (7 = sum of every viewer's run
      // count across the same prayer).
      if (isConsolidatedWatch) {
        const handles = (item._memberHandles || []).filter(Boolean);
        const preview = handles.slice(0, 2).map((h) => `@${h}`).join(', ');
        const extra = uniqueViewers - Math.min(2, handles.length);
        const who = handles.length === 0
          ? `${uniqueViewers} people`
          : (extra > 0
              ? `${preview} and ${extra} other${extra === 1 ? '' : 's'}`
              : preview);
        const suffix = watchCount > uniqueViewers ? ` ${watchCount} times` : '';
        return (
          <>
            <span className="font-medium">{who}</span>
            {' '}prayed for you{suffix}
            <span className="text-zinc-500"> · <span className="font-medium">{item.prayer_title}</span></span>
          </>
        );
      }
      // Single viewer, running count > 1: "prayed for you 5 times".
      const suffix = watchCount > 1 ? ` ${watchCount} times` : '';
      return <>prayed for you{suffix}<span className="text-zinc-500"> · <span className="font-medium">{item.prayer_title}</span></span></>;
    }
    if (isThanks) return <>{item.text}</>;
    if (isAdminNotify) return <>{item.text}</>;
    if (isAdminDaily) {
      const m = item.meta || {};
      const parts = [];
      if (m.misuse_reports_count) parts.push(`${m.misuse_reports_count} misuse report${m.misuse_reports_count === 1 ? '' : 's'}`);
      if (m.feedback_count) parts.push(`${m.feedback_count} feedback item${m.feedback_count === 1 ? '' : 's'}`);
      return <>Moderation summary — {parts.join(' · ') || 'no new items today'}</>;
    }
    return <>{item.text || 'New activity'}</>;
  })();

  const kindIcon = (() => {
    if (isComment) return <MessageCircle size={12} />;
    if (isNewPrayer) return <Sparkles size={12} />;
    if (isRandomShare) return <Share2 size={12} />;
    if (isRandomOpened) return <HandHeart size={12} />;
    if (isPrayerWatched) return <HandHelping size={12} />;
    if (isThanks) return <Heart size={12} />;
    if (isAdminNotify) return <Bell size={12} />;
    if (isAdminDaily) return <ShieldAlert size={12} />;
    return <Sparkles size={12} />;
  })();

  const kindLabel = (() => {
    if (isComment) return 'New comment';
    if (isNewPrayer) return 'New prayer';
    if (isRandomShare) return 'Random share';
    if (isRandomOpened) return 'Prayer opened';
    if (isPrayerWatched) return isConsolidatedWatch ? `Prayed for you (${watchCount})` : (watchCount > 1 ? `Prayed for you (${watchCount})` : 'Prayed for you');
    if (isThanks) return 'Thanks';
    if (isAdminNotify) return 'Admin message';
    if (isAdminDaily) return 'Daily digest';
    return 'Update';
  })();

  const linkClass = `flex items-start gap-3 px-4 py-3 rounded-xl border transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900/60 ${
    isUnread
      ? 'border-amber-200 dark:border-amber-900/60 bg-amber-50/40 dark:bg-amber-950/20'
      : 'border-zinc-200 dark:border-zinc-800 bg-transparent'
  }`;

  // Non-clickable rows for admin_daily / admin_notify (no prayer target).
  // Inline ternary keeps the component tree stable — avoids the
  // no-unstable-nested-components lint by not defining a Wrapper inside.
  const body = (
    <>
      <Avatar name={item.author_name} picture={item.author_picture} />
      <div className="min-w-0 flex-1">
        <div className="text-sm text-zinc-900 dark:text-zinc-100">
          {isConsolidatedWatch ? (
            // The label itself starts with the aggregate "who" phrase
            // ("@a, @b and 3 others"), so we skip the leading author_name
            // prefix that other row types get.
            label
          ) : (
            <>
              <span className="font-medium">{item.author_name}</span> {label}
            </>
          )}
        </div>
        {isComment && item.text && (
          <div className="mt-1 text-sm text-zinc-600 dark:text-zinc-400 line-clamp-2 whitespace-pre-wrap">
            &ldquo;{item.text}&rdquo;
          </div>
        )}
        <div className="mt-1 flex items-center gap-2 text-xs text-zinc-500 flex-wrap">
          <span className="inline-flex items-center gap-1">
            {kindIcon}
            {kindLabel}
          </span>
          {item.circle_name && (
            <>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <Users size={12} /> {item.circle_name}&apos;s circle
              </span>
            </>
          )}
          <span>·</span>
          <span title={item.created_at}>{timeAgo(item.created_at)}</span>
        </div>

        {/* "Send a thank you" reply. On single-viewer rows this fires
            one reply to the sole viewer. On consolidated rows it fans
            out to every viewer bundled into this aggregate — one
            thanks_reply notification per person. The label reflects
            how many recipients are still pending so the sender knows
            what tapping does. */}
        {isPrayerWatched && !isConsolidatedWatch && (
          <div className="mt-2">
            {thanksSent ? (
              <span
                data-testid={`thanks-sent-${item.id}`}
                className="inline-flex items-center gap-1 text-xs font-medium text-rose-500 dark:text-rose-400"
              >
                <Heart size={13} fill="currentColor" /> Thanks sent
              </span>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onThanks(item); }}
                data-testid={`send-thanks-btn-${item.id}`}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold transition-colors"
              >
                <Heart size={13} fill="currentColor" /> Send a thank you
              </button>
            )}
          </div>
        )}
        {isPrayerWatched && isConsolidatedWatch && (() => {
          const pendingCount = (item._pendingThanksIds || []).length;
          const sentCount = item._thanksSentCount || 0;
          const totalRecipients = uniqueViewers;
          const allSent = pendingCount === 0 && sentCount > 0;
          const members = Array.isArray(item._members) ? item._members : [];
          const expanded = Boolean(isExpanded);
          const canExpand = members.length > 0;
          return (
            <>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {allSent ? (
                  <span
                    data-testid={`thanks-sent-all-${item.id}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-rose-500 dark:text-rose-400"
                  >
                    <Heart size={13} fill="currentColor" /> Thanks sent to all {sentCount}
                  </span>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onThanks(item); }}
                      data-testid={`send-thanks-all-btn-${item.id}`}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold transition-colors"
                    >
                      <Heart size={13} fill="currentColor" />
                      {sentCount > 0
                        ? `Send thank you to remaining ${pendingCount}`
                        : `Send thank you to all ${totalRecipients}`}
                    </button>
                    {sentCount > 0 && (
                      <span className="text-xs text-zinc-500" data-testid={`thanks-progress-${item.id}`}>
                        {sentCount} sent
                      </span>
                    )}
                  </>
                )}
                {canExpand && onToggleExpanded && (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleExpanded(item.id); }}
                    data-testid={`show-who-btn-${item.id}`}
                    aria-expanded={expanded}
                    aria-controls={`show-who-list-${item.id}`}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-200 text-xs font-semibold transition-colors"
                  >
                    {expanded
                      ? <ChevronUp size={14} strokeWidth={2.5} />
                      : <ChevronDown size={14} strokeWidth={2.5} />}
                    {expanded ? 'Hide who' : `Show who (${members.length})`}
                  </button>
                )}
              </div>
              {expanded && canExpand && (
                <ul
                  id={`show-who-list-${item.id}`}
                  data-testid={`show-who-list-${item.id}`}
                  className="mt-2 space-y-1.5 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50/60 dark:bg-zinc-900/40 p-2"
                >
                  {members.map((m) => (
                    <li
                      key={m.notifId}
                      data-testid={`show-who-member-${m.notifId}`}
                      className="flex items-center gap-2 text-xs text-zinc-700 dark:text-zinc-200"
                    >
                      <Avatar name={m.name} picture={m.picture} size={20} />
                      <span className="min-w-0 flex-1 truncate">
                        <span className="font-medium">
                          {m.handle ? `@${m.handle}` : m.name}
                        </span>
                        <span className="text-zinc-500 dark:text-zinc-400">
                          {` · prayed ${m.watchCount} ${m.watchCount === 1 ? 'time' : 'times'}`}
                        </span>
                      </span>
                      {m.thanksSent ? (
                        <span
                          data-testid={`show-who-thanks-sent-${m.notifId}`}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-500 dark:text-rose-400 flex-shrink-0"
                        >
                          <Heart size={11} fill="currentColor" /> Thanked
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Fire an individual thanks by wrapping the
                            // member's single notif id in a synthetic
                            // consolidated-shaped payload so `handleThanks`
                            // reuses its existing fan-out (and its 409
                            // idempotency) without a new code path.
                            onThanks({
                              _isConsolidatedWatch: true,
                              _pendingThanksIds: [m.notifId],
                              _underlyingIds: [m.notifId],
                            });
                          }}
                          data-testid={`show-who-send-thanks-${m.notifId}`}
                          className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-[11px] font-semibold transition-colors flex-shrink-0"
                        >
                          <Heart size={11} fill="currentColor" /> Thank
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          );
        })()}
      </div>
      {isUnread && (
        <span
          aria-hidden
          className="mt-2 w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"
          data-testid={`notification-unread-dot-${item.id}`}
        />
      )}
    </>
  );

  const deleteBtn = (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete && onDelete(item); }}
      title="Delete this notification"
      aria-label="Delete this notification"
      data-testid={`notification-delete-btn-${item.id}`}
      className="absolute top-2 right-2 p-2 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-950/50 dark:hover:text-red-300 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700 hover:ring-red-300 dark:hover:ring-red-700 transition-colors"
    >
      <X size={18} strokeWidth={2.75} />
    </button>
  );

  if (isAdminDaily || isAdminNotify) {
    return (
      <div className="relative" data-testid={`notification-row-wrapper-${item.id}`}>
        <div data-testid={`notification-${item.id}`} className={linkClass}>{body}</div>
        {deleteBtn}
      </div>
    );
  }
  return (
    <div className="relative" data-testid={`notification-row-wrapper-${item.id}`}>
      <Link to={to} data-testid={`notification-${item.id}`} className={linkClass}>{body}</Link>
      {deleteBtn}
    </div>
  );
};

const Notifications = ({ embedded = false }) => {
  const { user, loading: authLoading, openAuthModal } = useAuth();
  const [items, setItems] = useState([]);
  const [lastReadAt, setLastReadAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  // Which tab is active. `members` covers new prayers, comments, random
  // shares, thanks, and prayer_watched events. `admin` covers messages
  // from the moderation team (admin_notify_user + admin_daily_summary).
  const [activeTab, setActiveTab] = useState('members');
  const [clearingAll, setClearingAll] = useState(false);
  // Set of consolidated `prayer_watched` aggregate ids that are
  // currently expanded to show the per-viewer roster. Keyed by the
  // aggregate's `id` (which is the id of the FIRST underlying row —
  // stable across re-renders while the row is on-screen).
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const toggleExpanded = useCallback((aggId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(aggId)) next.delete(aggId); else next.add(aggId);
      return next;
    });
  }, []);

  // Split items into member vs admin buckets. `admin` types are
  // hard-coded so the classifier stays explicit — anything unknown
  // falls through to the members tab so we don't accidentally hide it.
  const ADMIN_TYPES = new Set(['admin_notify_user', 'admin_daily_summary']);
  const isAdminItem = (it) => ADMIN_TYPES.has(it?.type);

  // Consolidate prayer_watched rows by prayer_id so a viral prayer
  // shows ONE consolidated row rather than a wall of near-duplicate
  // pings. Each underlying row now carries its own running
  // `meta.watch_count` (bumped every time that viewer re-watches).
  // The aggregate sums those counts to show a total "prayer sessions"
  // number across all viewers. Underlying `id`s are preserved so
  // Clear All can still atomically delete each row, and the batched
  // "Send thank you" button can dispatch one reply per underlying id.
  const consolidateItems = (rows) => {
    const out = [];
    const watchedByPrayer = new Map(); // prayer_id -> aggregate row
    for (const it of rows) {
      if (it?.type !== 'prayer_watched' || !it.prayer_id) {
        out.push(it);
        continue;
      }
      const rowCount = Math.max(1, Number(it?.meta?.watch_count) || 1);
      // Snapshot-based "already thanked" gate (Option A). Legacy rows
      // that carry `thanks_sent = true` but no snapshot are treated as
      // permanently thanked (backward-compat). New rows unlock the
      // button again as soon as the viewer's `watch_count` exceeds the
      // snapshot recorded at the last thank-time.
      const thanksSentFlag = Boolean((it.meta || {}).thanks_sent);
      const snapshotRaw = (it.meta || {}).thanks_sent_at_count;
      const snapshotCount = typeof snapshotRaw === 'number' ? snapshotRaw : null;
      const alreadyThanked = thanksSentFlag && (
        snapshotCount === null ? true : rowCount <= snapshotCount
      );
      let agg = watchedByPrayer.get(it.prayer_id);
      if (!agg) {
        agg = {
          ...it,
          _isConsolidatedWatch: true,
          _memberHandles: [],
          _memberNames: [],
          _memberIds: new Set(),
          // Per-viewer records — powers the "Show who" expanded view
          // and per-viewer thank-you affordances. One entry per unique
          // actor, in most-recent-first order (rebuilt via a
          // stable-sort pass after the loop).
          _members: [],
          _underlyingIds: [],
          _pendingThanksIds: [],   // subset that hasn't been thanked yet
          _thanksSentCount: 0,     // how many viewers we've already thanked
          _watchCount: 0,       // total prayer sessions across all viewers
          _uniqueViewerCount: 0, // distinct people who prayed the prayer
        };
        watchedByPrayer.set(it.prayer_id, agg);
        out.push(agg);
      }
      const actorId = it.actor_user_id || it.author_user_id || it.id;
      if (!agg._memberIds.has(actorId)) {
        agg._memberIds.add(actorId);
        agg._memberHandles.push(it.actor_handle || null);
        agg._memberNames.push(it.author_name || 'Someone');
        agg._members.push({
          notifId: it.id,
          userId: actorId,
          name: it.author_name || 'Someone',
          handle: it.actor_handle || null,
          picture: it.author_picture || null,
          watchCount: rowCount,
          thanksSent: alreadyThanked,
          createdAt: it.created_at || '',
        });
        agg._uniqueViewerCount += 1;
      }
      agg._watchCount += rowCount; // sum the per-viewer counters
      agg._underlyingIds.push(it.id);
      if (alreadyThanked) {
        agg._thanksSentCount += 1;
      } else {
        agg._pendingThanksIds.push(it.id);
      }
      if ((it.created_at || '') > (agg.created_at || '')) {
        agg.created_at = it.created_at;
      }
    }
    // Sort each aggregate's member list by most-recent first so the
    // expanded "Show who" view leads with the freshest activity.
    for (const agg of watchedByPrayer.values()) {
      agg._members.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    }
    out.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    return out;
  };

  const memberItems = consolidateItems(items.filter((it) => !isAdminItem(it)));
  const adminItems = items.filter(isAdminItem);
  const visibleItems = activeTab === 'admin' ? adminItems : memberItems;

  const handleThanks = async (item) => {
    // Consolidated prayer_watched row: fan out one `sendThanks` call per
    // underlying viewer notification that hasn't been thanked yet, in
    // parallel. Each successful reply marks that source row so the
    // per-viewer state stays truthful — if some fail, only the ones that
    // succeeded flip to `thanks_sent`.
    if (item?._isConsolidatedWatch) {
      const targetIds = (item._pendingThanksIds && item._pendingThanksIds.length)
        ? [...item._pendingThanksIds]
        : [...(item._underlyingIds || [])];
      if (!targetIds.length) return;
      const results = await Promise.allSettled(targetIds.map((id) => sendThanks(id)));
      const succeededIds = new Set();
      results.forEach((r, i) => {
        // Backend 409s if a specific viewer's row was already thanked —
        // treat that as an idempotent success so the UI settles to
        // "all thanked" even after a partial retry.
        const isDuplicate = r.status === 'rejected'
          && (r.reason?.response?.status === 409
              || (r.reason?.response?.data?.detail || '').toLowerCase().includes('already'));
        if (r.status === 'fulfilled' || isDuplicate) {
          succeededIds.add(targetIds[i]);
        }
      });
      if (succeededIds.size === 0) {
        alert('Could not send thanks. Please try again.');
        return;
      }
      setItems((prev) => prev.map((it) => {
        if (!succeededIds.has(it.id)) return it;
        // Stamp the snapshot with the row's CURRENT watch_count so the
        // client-side "already thanked" gate matches what the server
        // just persisted. Future re-prayers by this viewer will push
        // meta.watch_count above the snapshot and unlock the button
        // again after the next refresh.
        const currentCount = Math.max(1, Number(it?.meta?.watch_count) || 1);
        return {
          ...it,
          meta: {
            ...(it.meta || {}),
            thanks_sent: true,
            thanks_sent_at_count: currentCount,
          },
        };
      }));
      // If any target failed with a NON-duplicate error, surface a
      // gentle notice while still keeping the partial success in state.
      const trueFailures = results.filter((r, i) => (
        r.status === 'rejected' && !succeededIds.has(targetIds[i])
      ));
      if (trueFailures.length) {
        alert(`Sent thanks to ${succeededIds.size} of ${targetIds.length}. Please retry the rest.`);
      }
      return;
    }
    // Single-viewer row — unchanged behavior.
    try {
      await sendThanks(item.id);
      setItems((prev) => prev.map((it) => {
        if (it.id !== item.id) return it;
        const currentCount = Math.max(1, Number(it?.meta?.watch_count) || 1);
        return {
          ...it,
          meta: {
            ...(it.meta || {}),
            thanks_sent: true,
            thanks_sent_at_count: currentCount,
          },
        };
      }));
    } catch (e) {
      console.error('send thanks failed', e);
      alert(e?.response?.data?.detail || 'Could not send thanks.');
    }
  };

  const handleDelete = async (item) => {
    // For consolidated watch rows, we delete every underlying dn_* id
    // in parallel so the whole aggregate disappears at once. Regular
    // rows just delete their single id.
    const idsToDelete = item?._isConsolidatedWatch && item._underlyingIds?.length
      ? [...item._underlyingIds]
      : [item.id];
    // Optimistic removal — snapshot the current list so we can roll back
    // if the server call fails, otherwise the row goes immediately.
    const snapshot = items;
    setItems((prev) => prev.filter((it) => !idsToDelete.includes(it.id)));
    // Nudge the header badge down by the number of underlying rows so
    // the counter stays in step with the visible list. Fire one decrement
    // per underlying id since the badge tracks direct_notifications
    // row count. Server reconcile happens after the DELETE resolves.
    try {
      idsToDelete.forEach(() => {
        window.dispatchEvent(new Event('pwm:notifications-decrement'));
      });
    } catch { /* noop */ }
    try {
      await Promise.all(idsToDelete.map((id) => deleteNotification(id)));
      try {
        window.dispatchEvent(new Event('pwm:notifications-changed'));
      } catch { /* noop */ }
    } catch (e) {
      console.error('delete notification failed', e);
      setItems(snapshot);
      try {
        window.dispatchEvent(new Event('pwm:notifications-changed'));
      } catch { /* noop */ }
      alert(e?.response?.data?.detail || 'Could not delete notification.');
    }
  };

  const handleClearAll = async () => {
    // Collect every underlying `dn_*` / synthetic id from the currently
    // visible tab (already consolidated for the members view). Fires
    // parallel deletes with optimistic UI so the whole list vanishes at
    // once. On failure we snap the list back and let the header
    // reconcile against server truth.
    if (clearingAll) return;
    const list = activeTab === 'admin' ? adminItems : memberItems;
    if (!list.length) return;
    const idsToDelete = [];
    for (const it of list) {
      if (it?._isConsolidatedWatch && it._underlyingIds?.length) {
        idsToDelete.push(...it._underlyingIds);
      } else if (it?.id) {
        idsToDelete.push(it.id);
      }
    }
    if (!idsToDelete.length) return;
    const snapshot = items;
    setClearingAll(true);
    // Optimistic: drop every id we're about to delete from the visible
    // list. Non-tab items (e.g. admin rows while we're clearing the
    // members tab) stay untouched.
    setItems((prev) => prev.filter((it) => !idsToDelete.includes(it.id)));
    try {
      idsToDelete.forEach(() => {
        window.dispatchEvent(new Event('pwm:notifications-decrement'));
      });
    } catch { /* noop */ }
    try {
      await Promise.all(idsToDelete.map((id) => deleteNotification(id)));
      try { window.dispatchEvent(new Event('pwm:notifications-changed')); } catch { /* noop */ }
    } catch (e) {
      console.error('clear all failed', e);
      setItems(snapshot);
      try { window.dispatchEvent(new Event('pwm:notifications-changed')); } catch { /* noop */ }
      alert(e?.response?.data?.detail || 'Could not clear all notifications.');
    } finally {
      setClearingAll(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    getNotifications({ limit: 60 })
      .then((data) => {
        if (cancelled) return;
        setItems(Array.isArray(data.items) ? data.items : []);
        setLastReadAt(data.last_read_at || null);
        // Advance the read watermark to "now" — bell badge will clear on
        // next fetch. We do NOT clear the local `isUnread` styling for the
        // items we already see, so the visitor still knows which entries
        // arrived since their last visit.
        markNotificationsRead().catch(() => { /* non-fatal */ });
      })
      .catch(() => { if (!cancelled) setError('Could not load notifications.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, authLoading]);

  // Manual refresh — silent re-fetch triggered by the Refresh button
  // in the header. Uses a separate `refreshing` state so we can spin
  // the icon without collapsing the list back to the full-page loader.
  // Does NOT re-run markNotificationsRead so any items that arrived
  // after the initial visit keep their "new" styling until the user
  // navigates away and back.
  const handleRefresh = useCallback(() => {
    if (!user || refreshing) return;
    setRefreshing(true);
    getNotifications({ limit: 60 })
      .then((data) => {
        setItems(Array.isArray(data.items) ? data.items : []);
        setLastReadAt(data.last_read_at || null);
      })
      .catch(() => { setError('Could not refresh notifications.'); })
      .finally(() => { setRefreshing(false); });
  }, [user, refreshing]);

  if (!authLoading && !user) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="notifications-signin">
        <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-950/60 flex items-center justify-center text-amber-600 dark:text-amber-300 mb-3">
          <Bell size={24} />
        </div>
        <h1 className="text-lg font-semibold mb-1">Sign in to see notifications</h1>
        <p className="text-sm text-zinc-500 mb-4">
          We&apos;ll let you know when the people you follow share prayers or leave comments.
        </p>
        <button
          type="button"
          onClick={openAuthModal}
          data-testid="notifications-signin-btn"
          className="h-10 px-5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold"
        >
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div
      className={
        embedded
          ? 'py-2'
          : 'max-w-2xl mx-auto px-4 sm:px-6 py-6'
      }
      data-testid="notifications-page"
    >
      {!embedded && (
        <div className="flex items-center gap-2 mb-4">
          <Bell size={22} className="text-amber-500" />
          <h1 className="text-2xl font-bold" data-testid="notifications-title">Notifications</h1>
        </div>
      )}

      {/* Tab bar — "From Members" vs "From Admin". Counts reflect the
          current bucket after consolidation (consolidated prayer_watched
          rows count as 1). Selected tab gets amber underline. */}
      <div className="flex items-center gap-1 border-b border-zinc-200 dark:border-zinc-800 mb-4" data-testid="notifications-tabs">
        {[
          { key: 'members', label: 'From Members', count: memberItems.length },
          { key: 'admin', label: 'From Admin', count: adminItems.length },
        ].map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              data-testid={`notifications-tab-${tab.key}`}
              className={`relative px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'text-zinc-900 dark:text-white'
                  : 'text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200'
              }`}
              aria-selected={isActive}
              role="tab"
            >
              {tab.label}
              {tab.count > 0 && (
                <span
                  className={`ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${
                    isActive
                      ? 'bg-amber-500 text-white'
                      : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300'
                  }`}
                >
                  {tab.count > 99 ? '99+' : tab.count}
                </span>
              )}
              {isActive && (
                <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-amber-500" />
              )}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            data-testid="notifications-refresh-btn"
            title="Refresh notifications"
            aria-label="Refresh notifications"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500 hover:text-amber-600 dark:hover:text-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors py-2 px-2"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          {visibleItems.length > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              disabled={clearingAll}
              data-testid="notifications-clear-all-btn"
              className="text-xs font-medium text-zinc-500 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors py-2 px-2"
            >
              {clearingAll ? 'Clearing…' : 'Clear all'}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-zinc-500 flex items-center justify-center gap-2" data-testid="notifications-loading">
          <Loader2 className="animate-spin" size={16} /> Loading notifications…
        </div>
      ) : error ? (
        <div className="py-10 text-center text-red-600 dark:text-red-400" data-testid="notifications-error">{error}</div>
      ) : visibleItems.length === 0 ? (
        <div className="py-16 text-center" data-testid="notifications-empty">
          <div className="mx-auto w-14 h-14 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-400 mb-3">
            <Bell size={22} />
          </div>
          <h2 className="text-base font-medium mb-1">You&apos;re all caught up</h2>
          <p className="text-sm text-zinc-500 max-w-md mx-auto">
            {activeTab === 'admin' ? 'No messages from the moderation team.' : 'No notifications from members.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="notifications-list">
          {visibleItems.map((it) => (
            <NotificationRow
              key={it.id}
              item={it}
              isUnread={!lastReadAt || (it.created_at || '') > lastReadAt}
              onThanks={handleThanks}
              onDelete={handleDelete}
              isExpanded={expandedIds.has(it.id)}
              onToggleExpanded={toggleExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Notifications;
